/* =========================================================
   Toasts, themed per action kind:
     save / ok → green · delete → red
     template  → amber · import → blue
   Green and blue confirmations fire a confetti burst.
   ========================================================= */
import React, { type CSSProperties } from 'react';
import type { Toast as ToastModel, ToastKind } from '@/types';
import { ic, sized, type IconElement } from '@/components/Icons';
import { bellSound } from '@/lib/sfx';
import { useAppStore } from '@/store/useAppStore';

interface Theme {
  bg: string; border: string; fg: string; ring: string;
  body: string; icon: IconElement; shadow: string; confetti: boolean;
}

const TOAST_THEMES: Partial<Record<ToastKind, Theme>> = {
  ok:       { bg:'var(--ok-l)',      border:'var(--ok-d)',    fg:'var(--ok-d)', ring:'var(--ok)',      body:'#1d6b4f', icon: ic.check,    shadow:'rgba(4,120,87,.40)',  confetti:true  },
  save:     { bg:'var(--ok-l)',      border:'var(--ok-d)',    fg:'var(--ok-d)', ring:'var(--ok)',      body:'#1d6b4f', icon: ic.check,    shadow:'rgba(4,120,87,.40)',  confetti:true  },
  delete:   { bg:'var(--st-req-l)',  border:'var(--err)',     fg:'#b3261e',     ring:'var(--err)',     body:'#8f1d18', icon: ic.trash,    shadow:'rgba(220,38,38,.34)', confetti:false },
  template: { bg:'var(--st-edit-l)', border:'var(--st-edit)', fg:'#8a6508',     ring:'var(--st-edit)', body:'#7a5a06', icon: ic.download, shadow:'rgba(202,138,4,.32)', confetti:false },
  import:   { bg:'#E7F0FC',          border:'var(--info)',    fg:'#1a4f9c',     ring:'var(--info)',    body:'#1f4d8f', icon: ic.upload,   shadow:'rgba(30,91,191,.32)', confetti:true  },
};

const SOUND_KINDS: Partial<Record<ToastKind, true>> = { save: true, delete: true, template: true, import: true };

/* ---------- Confetti burst (success "detonations") ---------- */

const CONFETTI_COLORS = ['#059669', '#34D399', '#F26522', '#FBBF24', '#10B981', '#FFFFFF'];

function Confetti() {
  const pieces = React.useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => {
        const ang = (Math.PI * 2 * i) / 18 + (Math.random() - 0.5) * 0.5;
        const dist = 46 + Math.random() * 54;
        return {
          dx: Math.cos(ang) * dist + 'px',
          dy: Math.sin(ang) * dist - 10 + 'px',
          rot: Math.random() * 720 - 360 + 'deg',
          color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          delay: Math.random() * 0.05,
          dur: 0.7 + Math.random() * 0.5,
          w: 5 + Math.random() * 5,
          h: 7 + Math.random() * 6,
          round: Math.random() > 0.6,
        };
      }),
    [],
  );

  return (
    <div aria-hidden="true" style={{ position: 'absolute', left: 26, top: 24, width: 0, height: 0, pointerEvents: 'none', zIndex: 2 }}>
      {pieces.map((p, i) => (
        <span
          key={i}
          style={
            {
              position: 'absolute', width: p.w, height: p.h,
              background: p.color, borderRadius: p.round ? '50%' : 1,
              '--dx': p.dx, '--dy': p.dy, '--rot': p.rot,
              animation: `confettiPop ${p.dur}s cubic-bezier(.18,.7,.4,1) ${p.delay}s both`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

/* ---------- Toast ---------- */

function Toast({ t, dismiss }: { t: ToastModel; dismiss: (id: string) => void }) {
  const theme = t.kind ? TOAST_THEMES[t.kind] : undefined;
  const playSound = !!(t.celebrate || (t.kind && SOUND_KINDS[t.kind]));
  const showConfetti = !!theme?.confetti && (t.celebrate || t.kind === 'import');

  React.useEffect(() => {
    if (playSound) bellSound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (theme) {
    // Big themed toast — coloured fill + trim + animated icon stamp.
    return (
      <div
        className="card"
        role="status"
        style={{
          position: 'relative', overflow: 'visible',
          padding: '12px 20px', minWidth: 'min(324px, calc(100vw - 32px))', maxWidth: 380,
          background: theme.bg, border: `2px solid ${theme.border}`,
          borderRadius: 13, boxShadow: `0 10px 30px -8px ${theme.shadow}`,
          pointerEvents: 'auto', animation: 'okToastIn .42s cubic-bezier(.2,.8,.3,1) both',
        }}
      >
        {showConfetti ? <Confetti /> : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
          <span style={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `2px solid ${theme.ring}`, animation: 'okRing .7s ease-out .05s both' }} />
            <span
              style={{
                width: 30, height: 30, borderRadius: '50%', background: theme.border, color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                animation: 'okCheckPop .5s cubic-bezier(.2,.9,.3,1.4) both',
              }}
            >
              {sized(theme.icon, 17)}
            </span>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 17.5, color: theme.fg, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{t.title}</div>
            {t.body ? <div style={{ fontSize: 14.5, color: theme.body, marginTop: 3, fontWeight: 600, lineHeight: 1.3 }}>{t.body}</div> : null}
          </div>
          <button className="btn icon ghost" onClick={() => dismiss(t.id)} aria-label="close" style={{ padding: 5, color: theme.fg, alignSelf: 'flex-start' }}>
            {ic.close}
          </button>
        </div>
      </div>
    );
  }

  // default (err / warn / info)
  return (
    <div
      className="card"
      role="status"
      style={{
        padding: '10px 14px', minWidth: 240,
        borderLeft: `3px solid ${t.kind === 'err' ? 'var(--err)' : t.kind === 'warn' ? 'var(--warn)' : 'var(--brand-primary)'}`,
        boxShadow: 'var(--shadow-3)', pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</span>
        <button className="btn icon ghost" onClick={() => dismiss(t.id)} aria-label="close" style={{ padding: 4 }}>
          {ic.close}
        </button>
      </div>
      {t.body ? <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{t.body}</div> : null}
    </div>
  );
}

export function ToastHost() {
  const toasts = useAppStore((s) => s.toasts);
  const dismiss = useAppStore((s) => s.dismissToast);
  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed', top: 70, right: 20, maxWidth: 'calc(100vw - 32px)',
        display: 'flex', flexDirection: 'column', gap: 10, zIndex: 200, pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <Toast key={t.id} t={t} dismiss={dismiss} />
      ))}
    </div>
  );
}
